import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  AppPlatform, AppVersionsState, VERSION_PATTERN, VersionPolicies, compareVersions, getAppVersions, updateAppVersions
} from '../../api/services/appVersionsService';
import { BigButton, FIELD, FieldLabel, LineIcon, SectionLabel, StatusPill } from '../../components/ui/dg';

/**
 * Analytics › Versions (`?vue=versions`, administrateurs) : la version minimale
 * et la dernière version de l'app, par plateforme, avec les comptes actifs de
 * chaque version pour voir qui serait bloqué avant d'enregistrer.
 */

const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

// Première version de l'app qui sait afficher l'écran de mise à jour. Les
// versions plus anciennes, sous le minimum, reçoivent des erreurs sans
// explication : mieux vaut ne pas les bloquer tant qu'elles sont nombreuses.
const FIRST_VERSION_WITH_UPDATE_SCREEN = '1.4.5';

const PLATFORMS: { id: AppPlatform; label: string; available: string }[] = [
  {
    id: 'android',
    label: 'Android',
    available: 'Google Play, mise à jour flexible'
  },
  {
    id: 'ios',
    label: 'iPhone',
    available: 'App Store, invitation à mettre à jour'
  }
];

const EMPTY: VersionPolicies = {
  android: { min_version: null, latest_version: null },
  ios: { min_version: null, latest_version: null }
};

type Field = 'min_version' | 'latest_version';
type Draft = Record<AppPlatform, Record<Field, string>>;

const toDraft = (policies: VersionPolicies): Draft => ({
  android: { min_version: policies.android.min_version || '', latest_version: policies.android.latest_version || '' },
  ios: { min_version: policies.ios.min_version || '', latest_version: policies.ios.latest_version || '' }
});

const statusOf = (version: string | null, rule: Record<Field, string>) => {
  if (!version) return 'unknown';
  if (rule.min_version && VERSION_PATTERN.test(rule.min_version) && compareVersions(version, rule.min_version) < 0) return 'required';
  if (rule.latest_version && VERSION_PATTERN.test(rule.latest_version) && compareVersions(version, rule.latest_version) < 0) return 'available';
  return 'ok';
};

const STATUS_LABEL: Record<string, { text: string; className: string }> = {
  required: { text: 'Bloquée', className: 'bg-red-50 text-red-700' },
  available: { text: 'Invitée à mettre à jour', className: 'bg-amber-50 text-amber-800' },
  ok: { text: 'À jour', className: 'bg-green-50 text-green-700' },
  unknown: { text: 'Version inconnue', className: 'bg-gray-100 text-gray-500' }
};

const errorOf = (rule: Record<Field, string>): string | null => {
  if (rule.min_version && !VERSION_PATTERN.test(rule.min_version)) return 'Version minimale invalide : chiffres séparés par des points (1.4.5).';
  if (rule.latest_version && !VERSION_PATTERN.test(rule.latest_version)) return 'Dernière version invalide : chiffres séparés par des points (1.4.5).';
  if (rule.min_version && rule.latest_version && compareVersions(rule.min_version, rule.latest_version) > 0) {
    return 'La version minimale dépasse la dernière version.';
  }
  return null;
};

const AppVersions = () => {
  const { token } = useAuth();
  const [state, setState] = useState<AppVersionsState | null>(null);
  const [draft, setDraft] = useState<Draft>(toDraft(EMPTY));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    getAppVersions(token)
      .then((data) => { setState(data); setDraft(toDraft(data.policies)); })
      .catch((error) => setMessage({ tone: 'error', text: error.message }));
  }, [token]);

  const saved = toDraft(state?.policies || EMPTY);
  const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
  const invalid = PLATFORMS.some(({ id }) => errorOf(draft[id]));

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setMessage(null);
    try {
      const policies: VersionPolicies = {
        android: { min_version: draft.android.min_version || null, latest_version: draft.android.latest_version || null },
        ios: { min_version: draft.ios.min_version || null, latest_version: draft.ios.latest_version || null }
      };
      const data = await updateAppVersions(token, policies);
      setState(data);
      setDraft(toDraft(data.policies));
      // L'API garde la politique une minute en mémoire.
      setMessage({ tone: 'ok', text: 'Enregistré : pris en compte par l’app d’ici une minute.' });
    } catch (error: any) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  // Comptes qui passeraient sous la version minimale, toutes plateformes.
  const impact = PLATFORMS.map(({ id }) => {
    const rows = (state?.adoption[id] || []).filter((row) => statusOf(row.version, draft[id]) === 'required');
    return {
      users: rows.reduce((sum, row) => sum + row.users, 0),
      withoutScreen: rows.filter((row) => row.version && compareVersions(row.version, FIRST_VERSION_WITH_UPDATE_SCREEN) < 0).reduce((sum, row) => sum + row.users, 0)
    };
  });
  const blockedUsers = impact.reduce((sum, i) => sum + i.users, 0);
  const blockedWithoutScreen = impact.reduce((sum, i) => sum + i.withoutScreen, 0);
  const BAR = { required: '#F59E0B', available: '#A5A1F2', ok: '#4F46E5', unknown: '#C7C4F6' } as Record<string, string>;

  return (
    <main className="flex-1 space-y-5 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {PLATFORMS.map(({ id, label, available }) => {
          const rule = draft[id];
          const error = errorOf(rule);
          const adoption = state?.adoption[id] || [];
          const total = adoption.reduce((sum, row) => sum + row.users, 0);
          const lagging = adoption.some((row) => statusOf(row.version, rule) !== 'ok');
          return (
            <section key={id} className={`${card} px-7 pb-6 pt-[22px]`}>
              <div className="flex items-center gap-3.5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#EFEEFD]">
                  <LineIcon d={id === 'android' ? 'M6 10h12v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zM6 9a6 6 0 0 1 12 0zM9 6.5h.01M15 6.5h.01' : 'M8 3h8v18H8zM11 18h2'} size={22} color="#4F46E5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[20px] font-extrabold text-[#1E293B]">{label}</h2>
                  <p className="text-[13px] text-[#6B7280]">{available}</p>
                </div>
                {state && <StatusPill tone={lagging ? 'amber' : 'green'}>{lagging ? 'Versions en retard' : 'À jour'}</StatusPill>}
              </div>
              <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                <div>
                  <FieldLabel htmlFor={`${id}-min`}>Version minimale</FieldLabel>
                  <input id={`${id}-min`} placeholder="aucune" value={rule.min_version} className={`${FIELD} font-mono`}
                    onChange={(e) => setDraft((d) => ({ ...d, [id]: { ...d[id], min_version: e.target.value.trim() } }))} />
                  <p className="mt-1.5 text-[12.5px] leading-[1.45] text-[#6B7280]">En dessous, l’app bloque et demande la mise à jour.</p>
                </div>
                <div>
                  <FieldLabel htmlFor={`${id}-latest`}>Dernière version</FieldLabel>
                  <input id={`${id}-latest`} placeholder="aucune" value={rule.latest_version} className={`${FIELD} font-mono`}
                    onChange={(e) => setDraft((d) => ({ ...d, [id]: { ...d[id], latest_version: e.target.value.trim() } }))} />
                  <p className="mt-1.5 text-[12.5px] leading-[1.45] text-[#6B7280]">En dessous, l’app propose la mise à jour sans bloquer.</p>
                </div>
              </div>
              {error && <p className="mt-3 text-[12.5px] font-semibold text-[#B91C1C]">{error}</p>}
              <div className="-mx-7 my-5 h-2 bg-[#F1F2F8]" />
              <div className="flex items-center justify-between">
                <SectionLabel>Comptes actifs ces 30 derniers jours</SectionLabel>
                {state?.store_urls[id] && <a href={state.store_urls[id]} target="_blank" rel="noreferrer" className="text-[13px] font-bold text-[#4F46E5] hover:underline">Fiche du store</a>}
              </div>
              {!state ? (
                <div className="mt-3 h-24 animate-pulse rounded-xl bg-[#EEF0F5]" />
              ) : adoption.length === 0 ? (
                <p className="mt-3 text-[13px] text-[#6B7280]">Aucun compte vu sur {label}.</p>
              ) : adoption.map((row) => {
                const status = statusOf(row.version, rule);
                return (
                  <div key={row.version || 'inconnue'} className="mt-3 flex items-center gap-3" title={STATUS_LABEL[status].text}>
                    <span className="w-14 font-mono text-[13px] font-bold">{row.version || '?'}</span>
                    <span className="h-2 flex-1 rounded-full bg-[#EEF0F5]"><span className="block h-2 rounded-full" style={{ width: `${total ? Math.max((row.users / total) * 100, 2) : 0}%`, background: BAR[status] }} /></span>
                    <span className="w-[110px] text-right text-[13px] text-[#6B7280]">{row.users} compte{row.users > 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>

      {(dirty || message) && (
        <section className={`${card} flex flex-wrap items-center gap-3.5 px-6 py-4`}>
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${blockedUsers ? 'bg-[#FFF4DB]' : 'bg-[#ECFDF5]'}`}>
            <LineIcon d={blockedUsers ? 'M12 3l9 16H3zM12 10v4M12 17v.5' : 'M5 12.5l4.5 4.5L19 7.5'} size={18} color={blockedUsers ? '#B45309' : '#047857'} />
          </span>
          <div className="min-w-0 flex-1">
            {message ? (
              <b className={`text-[14px] ${message.tone === 'ok' ? 'text-[#047857]' : 'text-[#B91C1C]'}`}>{message.text}</b>
            ) : blockedUsers ? (
              <>
                <b className="block text-[14px]">{blockedUsers} compte{blockedUsers > 1 ? 's' : ''} passeron{blockedUsers > 1 ? 't' : ''} sur l’écran de mise à jour</b>
                <span className="block text-[12.5px] text-[#6B7280]">
                  {blockedWithoutScreen > 0
                    ? `Dont ${blockedWithoutScreen} sur une version antérieure à ${FIRST_VERSION_WITH_UPDATE_SCREEN}, qui ne sait pas l’afficher et ne verra que des erreurs.`
                    : 'Une fois enregistré, d’ici une minute.'}
                </span>
              </>
            ) : (
              <b className="block text-[14px]">Aucun compte actif ne sera bloqué</b>
            )}
          </div>
          {dirty && (
            <>
              <BigButton kind="secondary" className="h-11" onClick={() => { setDraft(saved); setMessage(null); }}>Annuler</BigButton>
              <BigButton className="h-11 min-w-[180px]" disabled={invalid || saving} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer'}</BigButton>
            </>
          )}
        </section>
      )}
    </main>
  );
};

export default AppVersions;
